import React, { useEffect, useReducer } from 'react';
import { logError } from 'logging';
import {
  FILTERED_ITEM_NAV_BAR,
  MENU_NAV_BAR
} from 'bento-ordering/context/MenuContext';
import { FuseSearchService } from 'bento-ordering/utils/search';

function reducer(state, action) {
  switch (action.type) {
    case 'setSearchTerm':
      return { ...state, searchTerm: action.payload };
    case 'setFilteredMenu':
      return { ...state, filteredMenu: action.payload };
    case 'setIsFilteringInProgress':
      return { ...state, isFilteringInProgress: action.payload };
    case 'setMenuNavType':
      return { ...state, menuNavType: action.payload };
    case 'setFilteredPromotion':
      return { ...state, filteredPromotion: action.payload };
    default:
      logError('No type was provided to Menu Context reducer');
  }
}

export const useFilteredMenu = menu => {
  const initialState = {
    searchTerm: '',
    filteredMenu: menu,
    isFilteringInProgress: false,
    menuNavType: MENU_NAV_BAR,
    filteredPromotion: null
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSearchTerm = searchTerm => {
    dispatch({ type: 'setSearchTerm', payload: searchTerm });
  };

  const setFilteredMenu = menu => {
    dispatch({ type: 'setFilteredMenu', payload: menu });
  };

  const setIsFilteringInProgress = isFilteringInProgress => {
    dispatch({
      type: 'setIsFilteringInProgress',
      payload: isFilteringInProgress
    });
  };

  const setMenuNavType = menuNavType => {
    dispatch({ type: 'setMenuNavType', payload: menuNavType });
  };

  const setFilteredPromotion = filteredPromotion => {
    dispatch({ type: 'setFilteredPromotion', payload: filteredPromotion });
  };

  const previousSearchTerm = React.useRef('');
  const previousMenuRef = React.useRef();

  useEffect(() => {
    if (state.menuNavType === FILTERED_ITEM_NAV_BAR || !menu) {
      return;
    }

    if (menu?.sections?.length && !FuseSearchService.hasFuseSearchInstance()) {
      FuseSearchService.initializeFuse(menu);
    }

    if (
      previousMenuRef.current !== menu &&
      FuseSearchService.hasFuseSearchInstance()
    ) {
      FuseSearchService.resetFuseCollection(menu);
      previousMenuRef.current = menu;
    }

    let isComponentMounted = true;

    const fuzzySearchMenu = async () => {
      setIsFilteringInProgress(true);
      const fuzzySearchResult = await FuseSearchService.debouncedFuzzySearch(
        state.searchTerm,
        menu
      );
      setIsFilteringInProgress(false);
      return fuzzySearchResult;
    };

    /**
     * If there's a search term and it hasn't change from the previous render
     * we don't want to filter the menu again.
     * If the searchTerm changed before fuzzySearchMenu returns result
     * we also don't want to filter the menu using outdated result
     */

    if (
      state.searchTerm.length &&
      previousSearchTerm.current !== state.searchTerm
    ) {
      previousSearchTerm.current = state.searchTerm;
      fuzzySearchMenu().then(fuzzySearchResult => {
        if (
          isComponentMounted &&
          previousSearchTerm.current === state.searchTerm
        ) {
          setFilteredMenu(fuzzySearchResult);
        }
      });
    } else if (state.searchTerm === '') {
      setFilteredMenu(menu);
    }

    return () => {
      isComponentMounted = false; // Add return func to prevent memory leak
    };
  }, [state.searchTerm, menu, state.menuNavType]);

  return {
    setSearchTerm,
    setFilteredMenu,
    setIsFilteringInProgress,
    setMenuNavType,
    setFilteredPromotion,
    state
  };
};
